/**
 * Addressing rules for price model input metadata, shared by the server that
 * validates and stamps provenance and the client that keeps it aligned with the
 * form. Both sides must agree on what a schema path resolves to and on which
 * leaves count as empty, or the client submits entries the server rejects.
 */

/**
 * Encodes a schema path as a key usable for map lookups and set membership.
 * @param schemaPath - The path segments addressing a price model input leaf
 * @returns The encoded lookup key
 */
export function metadataPathKey(schemaPath: string[]): string {
  return JSON.stringify(schemaPath);
}

/**
 * Works out where each of an array field's rows ended up, so provenance can
 * follow a row that was dragged. Rows that survived the change unedited are
 * paired on their whole serialised contents; whatever is left over is paired in
 * the order it appears, which is how an edited row is recognised as the same row.
 * @param previousValue - The field's value before the change
 * @param nextValue - The field's value after the change
 * @returns Previous row index to next row index, or undefined when the field is not a reordered array
 */
export function matchRowMoves(
  previousValue: unknown,
  nextValue: unknown,
): Map<string, string> | undefined {
  if (!Array.isArray(previousValue) || !Array.isArray(nextValue)) {
    return undefined;
  }
  const previousRows = previousValue.map((row) => JSON.stringify(row));
  const nextRows = nextValue.map((row) => JSON.stringify(row));
  if (
    previousRows.length === nextRows.length &&
    previousRows.every((row, index) => row === nextRows[index])
  ) {
    return undefined;
  }

  const claimed = new Set<number>();
  const moves = new Map<string, string>();
  const claim = (previousIndex: number, nextIndex: number): void => {
    claimed.add(nextIndex);
    moves.set(String(previousIndex), String(nextIndex));
  };

  // A row still sitting where it was keeps its position, so identical rows
  // elsewhere cannot drag its provenance away.
  previousRows.forEach((row, index) => {
    if (row === nextRows[index]) {
      claim(index, index);
    }
  });
  previousRows.forEach((row, previousIndex) => {
    if (moves.has(String(previousIndex))) {
      return;
    }
    const nextIndex = nextRows.findIndex(
      (candidate, index) => candidate === row && !claimed.has(index),
    );
    if (nextIndex !== -1) {
      claim(previousIndex, nextIndex);
    }
  });

  const spareNextRows = nextRows
    .map((_row, index) => index)
    .filter((index) => !claimed.has(index));
  previousRows
    .map((_row, index) => index)
    .filter((index) => !moves.has(String(index)))
    .forEach((previousIndex, offset) => {
      const nextIndex = spareNextRows[offset];
      if (nextIndex !== undefined) {
        claim(previousIndex, nextIndex);
      }
    });
  return moves;
}

/**
 * Rewrites metadata schema paths so provenance follows rows that were only
 * reordered, and drops the paths of rows that are gone.
 * @param schemaPaths - The paths to rebase
 * @param previousInputs - The inputs the paths currently address
 * @param nextInputs - The inputs the paths should address
 * @returns The rebased paths in the order given, undefined where the row no longer exists
 */
export function rebaseSchemaPathsForReorder(
  schemaPaths: string[][],
  previousInputs: unknown,
  nextInputs: unknown,
): (string[] | undefined)[] {
  if (!isInputRecord(previousInputs) || !isInputRecord(nextInputs)) {
    return schemaPaths;
  }
  const movesByField = new Map(
    Object.keys(nextInputs).flatMap((field) => {
      const moves = matchRowMoves(previousInputs[field], nextInputs[field]);
      return moves ? [[field, moves] as const] : [];
    }),
  );
  if (movesByField.size === 0) {
    return schemaPaths;
  }
  return schemaPaths.map((schemaPath) => {
    const [field, row, ...rest] = schemaPath;
    const moves = movesByField.get(field);
    // A top-level primitive has no row segment, and its field never moves rows.
    if (!moves || schemaPath.length < 2) {
      return schemaPath;
    }
    const movedRow = moves.get(row);
    // Every surviving row is claimed by the moves map, so an absent row is gone.
    if (movedRow === undefined) {
      return undefined;
    }
    // A row that stayed put keeps its original path, so callers can tell an
    // untouched entry from a rebased one by identity.
    return movedRow === row ? schemaPath : [field, movedRow, ...rest];
  });
}

/**
 * Walks an input object by schema path, reporting cleared leaves as absent.
 * @param schemaPath - The path to walk
 * @param inputs - The inputs to walk into
 * @returns The addressed value, or undefined when the path does not resolve or the leaf is cleared
 */
export function resolveInputLeafValue(
  schemaPath: string[],
  inputs: unknown,
): unknown {
  const value = schemaPath.reduce<unknown>((current, segment) => {
    if (Array.isArray(current)) {
      return current[Number(segment)];
    }
    return isInputRecord(current) ? current[segment] : undefined;
  }, inputs);
  return isClearedInputValue(value) ? undefined : value;
}

/**
 * Reports whether a leaf holds no value. The JSON schema form clears a field by
 * emptying it rather than deleting its key, leaving `null` behind for numbers
 * and a blank string for text, while `0` and `false` stay real inputs.
 * @param value - The resolved leaf value
 * @returns True when the leaf carries no value
 */
export function isClearedInputValue(value: unknown): boolean {
  if (value === undefined || value === null) {
    return true;
  }
  return typeof value === "string" && value.trim() === "";
}

/**
 * Compares two leaf values structurally, treating undefined and null as equal.
 * @param a - The first value
 * @param b - The second value
 * @returns True when the values are equivalent
 */
export function isSameLeafValue(a: unknown, b: unknown): boolean {
  return (a ?? null) === (b ?? null);
}

/**
 * Narrows a value to a plain object, which array elements and nested inputs are
 * walked as. Arrays are excluded so index segments stay handled positionally.
 * @param value - The value to test
 * @returns True when the value is a non-array object
 */
export function isInputRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
